import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { curadoriaService } from '@/services/curadoriaService'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import { CuradoriaFila, CuradoriaReserva, STATUS_CURADORIA_COR, STATUS_CURADORIA_LABEL } from '@/types/curadoria'
import { CANAL_LABEL, CanalComercial, STATUS_LABEL } from '@/types/statusReserva'
import { formatCNPJ, formatCurrency, formatDateTime } from '@/utils/formatters'
import { ChipsValidacaoDocumental } from '@/components/curadoria/ChipsValidacaoDocumental'

/**
 * Validação de reservas (back-office, 06/09) — tela PRÓPRIA, separada da curadoria:
 * lista as reservas que o consultor concluiu e que aguardam a validação do time
 * Originei (`ENVIADA` = "Aguardando aprovação") e as que o time já assumiu
 * (`EM_ANALISE_ORIGINEI` = "Em análise"). Abrir uma linha leva à tela de validação
 * (`/validacao-reservas/:id`), onde a reserva é posta em análise, conferida — dados,
 * diagnóstico, checklist e arquivos — e aprovada, devolvida ou reprovada.
 *
 * Fonte: a mesma `GET /v1/oportunidades/curadoria` (escopo do token; back-office global).
 * "Com pendência" (devolvida ao consultor) fica visível num filtro à parte, para
 * acompanhamento, mas não é "aguardando validação".
 *
 * P3: cada linha mostra os contadores da validação documental (`itensAguardandoValidacao`,
 * `itensRecusados`, do backend) e o filtro "Documentos a validar" pede ao SERVIDOR só as linhas
 * com item RECEBIDO esperando o Operador (`?checklist=AGUARDANDO_VALIDACAO`).
 */

type Filtro = 'AGUARDANDO' | 'EM_ANALISE' | 'COM_PENDENCIA' | 'DOCS_A_VALIDAR' | 'TODAS'

const FILTRO_LABEL: Record<Filtro, string> = {
  AGUARDANDO: 'Aguardando aprovação',
  EM_ANALISE: 'Em análise',
  COM_PENDENCIA: 'Com pendência',
  DOCS_A_VALIDAR: 'Documentos a validar',
  TODAS: 'Todas',
}

const rotuloCanal = (canal: string | null): string =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : '—'

const nomeEmpresa = (r: CuradoriaReserva): string =>
  r.razaoSocial || r.nomeFantasia || (r.cnpj ? formatCNPJ(r.cnpj) : 'Empresa sem nome')

const formatNumero = (numero: number | null) =>
  numero != null ? `RES-${String(numero).padStart(4, '0')}` : '—'

const passaNoFiltro = (r: CuradoriaReserva, filtro: Filtro): boolean => {
  switch (filtro) {
    case 'AGUARDANDO':
      return r.status === 'ENVIADA'
    case 'EM_ANALISE':
      return r.status === 'EM_ANALISE_ORIGINEI'
    case 'COM_PENDENCIA':
      return r.status === 'COM_PENDENCIA'
    // O recorte já veio do servidor (`?checklist=AGUARDANDO_VALIDACAO`).
    case 'DOCS_A_VALIDAR':
    default:
      return true
  }
}

const ValidacaoReservasListPage: FC = () => {
  const navigate = useNavigate()
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  const backoffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'

  const [fila, setFila] = useState<CuradoriaFila | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('AGUARDANDO')
  /** Fila filtrada no servidor por `?checklist=AGUARDANDO_VALIDACAO` (só no filtro "Documentos a validar"). */
  const [filaDocs, setFilaDocs] = useState<CuradoriaFila | null>(null)
  const [carregandoDocs, setCarregandoDocs] = useState(false)
  const [erroDocs, setErroDocs] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setFila(await curadoriaService.fila())
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      setErro(msg || 'Não foi possível carregar as reservas aguardando validação.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    if (backoffice) void carregar()
  }, [backoffice, carregar])

  const carregarDocs = useCallback(async () => {
    setCarregandoDocs(true)
    setErroDocs(null)
    try {
      setFilaDocs(await curadoriaService.fila('AGUARDANDO_VALIDACAO'))
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      setErroDocs(msg || 'Não foi possível carregar as reservas com documentos a validar.')
    } finally {
      setCarregandoDocs(false)
    }
  }, [])

  useEffect(() => {
    if (backoffice && filtro === 'DOCS_A_VALIDAR') void carregarDocs()
  }, [backoffice, filtro, carregarDocs])

  // A fila traz também os rascunhos desde 09/09 (para Reservas e Curadoria); aqui só o
  // que o consultor já enviou — rascunho não tem dados nem documentos fechados para validar.
  const todas = useMemo(() => (fila?.reservas ?? []).filter((r) => r.status !== 'RASCUNHO'), [fila])
  const contagem = useMemo(
    () => ({
      aguardando: todas.filter((r) => r.status === 'ENVIADA').length,
      emAnalise: todas.filter((r) => r.status === 'EM_ANALISE_ORIGINEI').length,
      comPendencia: todas.filter((r) => r.status === 'COM_PENDENCIA').length,
    }),
    [todas]
  )
  const linhas = useMemo(
    () =>
      (filtro === 'DOCS_A_VALIDAR'
        ? (filaDocs?.reservas ?? []).filter((r) => r.status !== 'RASCUNHO')
        : todas.filter((r) => passaNoFiltro(r, filtro))
      )
        // Mais antiga primeiro: quem espera há mais tempo é validada antes.
        .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)),
    [todas, filtro, filaDocs]
  )
  const carregandoLista = filtro === 'DOCS_A_VALIDAR' ? carregandoDocs || (!filaDocs && !erroDocs) : carregando
  const erroLista = filtro === 'DOCS_A_VALIDAR' ? erroDocs : erro
  const recarregarLista = () => void (filtro === 'DOCS_A_VALIDAR' ? carregarDocs() : carregar())

  if (!backoffice) return <Navigate to="/reservas" replace />

  return (
    <Box>
      <PageHeader
        title="Validação de reservas"
        subtitle="Reservas concluídas pelos consultores e aguardando a validação do time Originei — confira dados e documentos antes de aprovar"
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <KpiCard
          label="Aguardando aprovação"
          valor={fila ? contagem.aguardando : '—'}
          legenda="enviadas e ainda não assumidas"
          variante="escura"
          carregando={carregando}
        />
        <KpiCard
          label="Em análise"
          valor={fila ? contagem.emAnalise : '—'}
          legenda="assumidas pelo time"
          carregando={carregando}
        />
        <KpiCard
          label="Com pendência"
          valor={fila ? contagem.comPendencia : '—'}
          legenda="devolvidas ao consultor"
          carregando={carregando}
        />
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={filtro}
        onChange={(_, v: Filtro | null) => {
          if (v) setFiltro(v)
        }}
        aria-label="Filtro de situação"
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        {(Object.keys(FILTRO_LABEL) as Filtro[]).map((f) => (
          <ToggleButton key={f} value={f}>
            {FILTRO_LABEL[f]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {carregandoLista ? (
        <EstadoDeLista estado="carregando" />
      ) : erroLista ? (
        <EstadoDeLista
          estado="erro"
          mensagem={erroLista}
          acao={{ label: 'Tentar novamente', onClick: recarregarLista }}
        />
      ) : linhas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem={
            filtro === 'AGUARDANDO'
              ? 'Nenhuma reserva aguardando aprovação'
              : `Nenhuma reserva em "${FILTRO_LABEL[filtro]}"`
          }
          detalhe="Reservas enviadas pelos consultores aparecem aqui até serem aprovadas, devolvidas ou reprovadas."
        />
      ) : (
        <TabelaAcao<CuradoriaReserva>
          aria-label="Reservas aguardando validação"
          linhas={linhas}
          chaveDaLinha={(r) => r.id}
          onAbrir={(r) => navigate(`/validacao-reservas/${r.id}`)}
          renderAcao={(r) => (r.status === 'ENVIADA' ? 'Validar →' : 'Abrir →')}
          colunas={[
            {
              titulo: 'Reserva',
              render: (r) => (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {nomeEmpresa(r)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.cnpj ? formatCNPJ(r.cnpj) : '—'} · {formatNumero(r.numero)}
                  </Typography>
                </Box>
              ),
            },
            { titulo: 'Produto', render: (r) => r.tipoNecessidade ?? '—' },
            {
              titulo: 'Valor',
              align: 'right',
              render: (r) => (r.valorEstimado != null ? formatCurrency(r.valorEstimado) : '—'),
            },
            { titulo: 'Canal', render: (r) => rotuloCanal(r.canal) },
            {
              titulo: 'Documentos',
              render: (r) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={r.documentosObrigatoriosFaltantes === 0 ? 'success' : 'warning'}
                    label={
                      r.documentosObrigatoriosTotal === 0
                        ? 'Sem exigências'
                        : `${r.documentosObrigatoriosTotal - r.documentosObrigatoriosFaltantes} de ${r.documentosObrigatoriosTotal}`
                    }
                  />
                  <ChipsValidacaoDocumental r={r} />
                </Stack>
              ),
            },
            {
              titulo: 'Situação',
              render: (r) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <Chip size="small" variant="outlined" label={STATUS_LABEL[r.status] ?? r.status} />
                  {r.statusCuradoria !== 'EM_ANALISE' && (
                    <Chip
                      size="small"
                      color={STATUS_CURADORIA_COR[r.statusCuradoria]}
                      label={STATUS_CURADORIA_LABEL[r.statusCuradoria]}
                    />
                  )}
                </Stack>
              ),
            },
            { titulo: 'Última movimentação', render: (r) => formatDateTime(r.updatedAt) },
          ]}
        />
      )}
    </Box>
  )
}

export default ValidacaoReservasListPage
